/**
 * 権限マトリクス（ユーザー／チーム × クライアントID）の操作。
 *
 * 全選択・全解除や「許可」チェックの副作用は取り違えると顧客の権限が
 * 意図せず広がる（または狭まる）ので、DOM に依存しない純粋関数にして
 * テストで固定する。
 */
import type { PermissionState } from "../api.js";

export type Subject = "users" | "teams";

/** 空の状態。読み込み前の初期値 */
export const emptyPermissionState = (): PermissionState => ({
  mode: "open",
  users: { allowed: [], clientIds: {} },
  teams: { allowed: [], clientIds: {} },
});

const uniq = (a: string[]) => [...new Set(a)];

/** 許可フラグの切り替え */
export function toggleAllowed(
  state: PermissionState,
  subject: Subject,
  id: string,
  next: boolean,
): PermissionState {
  const cur = state[subject];
  const allowed = next ? uniq([...cur.allowed, id]) : cur.allowed.filter((x) => x !== id);

  // 許可を外したら割り当ても消す。残しておくと「未許可なのに割り当てがある」
  // という状態になり、判定側では割り当てが許可の表明として扱われるため
  // 画面と挙動が食い違う
  const clientIds = { ...cur.clientIds };
  if (!next) delete clientIds[id];

  return { ...state, [subject]: { allowed, clientIds } };
}

/** クライアントID1つの割り当てを切り替え */
export function toggleClientId(
  state: PermissionState,
  subject: Subject,
  id: string,
  clientIdId: string,
  next: boolean,
): PermissionState {
  const cur = state[subject];
  const list = cur.clientIds[id] ?? [];
  const nextList = next ? uniq([...list, clientIdId]) : list.filter((x) => x !== clientIdId);

  const clientIds = { ...cur.clientIds };
  if (nextList.length > 0) clientIds[id] = nextList;
  else delete clientIds[id];

  // 割り当てたなら許可も立てる（restricted で「割り当てたのに使えない」を防ぐ）
  const allowed = next ? uniq([...cur.allowed, id]) : cur.allowed;

  return { ...state, [subject]: { allowed, clientIds } };
}

/**
 * 行の全選択（この対象に全クライアントIDを割り当てる）。
 *
 * 解除した場合は割り当てを空にする。**許可は外さない**
 * （空 = 全クライアントID可の意味になるため、許可したまま絞りを外す操作）。
 */
export function setRowAll(
  state: PermissionState,
  subject: Subject,
  id: string,
  allClientIds: string[],
  next: boolean,
): PermissionState {
  const cur = state[subject];
  const clientIds = { ...cur.clientIds };
  if (next) clientIds[id] = [...allClientIds];
  else delete clientIds[id];

  const allowed = next ? uniq([...cur.allowed, id]) : cur.allowed;
  return { ...state, [subject]: { allowed, clientIds } };
}

/**
 * 列の全選択（このクライアントIDを全対象に割り当てる）。
 *
 * @param ids 対象の全 ID（画面に出ているユーザー／チーム）
 */
export function setColumnAll(
  state: PermissionState,
  subject: Subject,
  clientIdId: string,
  ids: string[],
  next: boolean,
): PermissionState {
  let out = state;
  for (const id of ids) {
    out = toggleClientId(out, subject, id, clientIdId, next);
  }
  return out;
}

/** 「すべて許可」／「すべて解除」 */
export function setAllAllowed(
  state: PermissionState,
  subject: Subject,
  ids: string[],
  next: boolean,
): PermissionState {
  let out = state;
  for (const id of ids) {
    out = toggleAllowed(out, subject, id, next);
  }
  return out;
}

export function isAllowed(state: PermissionState, subject: Subject, id: string): boolean {
  return state[subject].allowed.includes(id);
}

export function hasClientId(
  state: PermissionState,
  subject: Subject,
  id: string,
  clientIdId: string,
): boolean {
  return (state[subject].clientIds[id] ?? []).includes(clientIdId);
}

/**
 * 1 行の状態を言葉で説明する。
 * チェックボックスだけでは「空 = 全部使える」が伝わらない。
 */
export function describeRow(
  state: PermissionState,
  subject: Subject,
  id: string,
  clientIdCount: number,
): string {
  const allowed = isAllowed(state, subject, id);
  const assigned = (state[subject].clientIds[id] ?? []).length;

  if (state.mode === "restricted" && !allowed) return "利用不可";
  if (assigned === 0) return `全クライアントID（${clientIdCount} 件）`;
  return `${assigned} / ${clientIdCount} 件`;
}

/**
 * 選択した対象に、指定したクライアントIDの集合を「そのまま」割り当てる（一括適用）。
 *
 * 空配列を渡すと「全クライアントID可」に戻す（絞りを外す）。許可は立てる。
 * 「選択 → まとめて設定」で使う。個々の現在値は上書きする（追加ではなく置換）。
 */
export function assignClientIdsTo(
  state: PermissionState,
  subject: Subject,
  ids: string[],
  clientIdIds: string[],
): PermissionState {
  const cur = state[subject];
  const clientIds = { ...cur.clientIds };
  const allowed = uniq([...cur.allowed, ...ids]);

  for (const id of ids) {
    if (clientIdIds.length > 0) clientIds[id] = uniq(clientIdIds);
    else delete clientIds[id]; // 空 = 全クライアントID可
  }
  return { ...state, [subject]: { allowed, clientIds } };
}
